import type { IQBLinkageHandler } from '../types';
import { hasNonEmptyRecordInLayers, metricLikeRemovedFields, patchBoth } from './shared';

function pruneStatistics(
  statistics: Record<string, string[]> | undefined,
  removedNames: Set<string>,
): Record<string, string[]> | undefined {
  if (!statistics) {
    return undefined;
  }
  const next: Record<string, string[]> = {};
  let changed = false;
  for (const [key, values] of Object.entries(statistics)) {
    const filtered = (values || []).filter((v) => !removedNames.has(v));
    if (filtered.length !== (values || []).length) {
      changed = true;
    }
    if (filtered.length) {
      next[key] = filtered;
    }
  }
  return changed ? next : undefined;
}

export const statisticsHandler: IQBLinkageHandler = {
  id: 'statistics',
  phase: 'prune',
  keys: ['statistics'],
  subscribe: ['metricRemoved', 'rawColumnRemoved'],
  apply(changeSet, ctx) {
    if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'statistics')) {
      return;
    }
    const removed = metricLikeRemovedFields(changeSet);
    if (!removed.length) {
      return;
    }
    const names = new Set(removed.map((m) => m.column_name));
    const baseNext = pruneStatistics(
      ctx.base.statistics as Record<string, string[]> | undefined,
      names,
    );
    const draftNext = pruneStatistics(
      ctx.draft.statistics as Record<string, string[]> | undefined,
      names,
    );
    return patchBoth(
      baseNext ? { statistics: baseNext } : undefined,
      draftNext ? { statistics: draftNext } : undefined,
    );
  },
};

export const statisticsMigrateHandler: IQBLinkageHandler = {
  id: 'statistics-migrate',
  phase: 'migrate',
  keys: ['statistics'],
  subscribe: ['fieldIdentityRenamed'],
  apply(changeSet, ctx) {
    if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'statistics')) {
      return;
    }
    const renames = changeSet.renamed.filter((r) => !r.displayOnly);
    if (!renames.length) {
      return;
    }
    const mapName = (name: string): string => {
      const hit = renames.find((r) => r.prevColumnName === name);
      return hit ? hit.nextColumnName : name;
    };
    const migrateLayer = (
      statistics: Record<string, string[]> | undefined,
    ): Record<string, string[]> | undefined => {
      if (!statistics) {
        return undefined;
      }
      const next: Record<string, string[]> = {};
      let changed = false;
      for (const [key, values] of Object.entries(statistics)) {
        const mapped = (values || []).map((v) => {
          const m = mapName(v);
          if (m !== v) {
            changed = true;
          }
          return m;
        });
        next[key] = mapped;
      }
      return changed ? next : undefined;
    };
    const baseNext = migrateLayer(
      ctx.base.statistics as Record<string, string[]> | undefined,
    );
    const draftNext = migrateLayer(
      ctx.draft.statistics as Record<string, string[]> | undefined,
    );
    return patchBoth(
      baseNext ? { statistics: baseNext } : undefined,
      draftNext ? { statistics: draftNext } : undefined,
    );
  },
};
